// utils/timeRestriction.js
/**
 * Time restriction utility for handling strategy availability based on NY timezone
 * Supports daylight saving time automatically
 */

/**
 * Check if current time falls within strategy's time restriction
 * @param {Object} timeRestriction - Time restriction config from strategy
 * @param {number} timeRestriction.hour_start - Start hour (0-23)
 * @param {number} timeRestriction.hour_end - End hour (0-23)
 * @param {number} timeRestriction.minute_start - Start minute (0-59)
 * @param {number} timeRestriction.minute_end - End minute (0-59)
 * @param {string} timeRestriction.timezone - IANA timezone (e.g., "America/New_York")
 * @returns {Object} Status object with isActive, timeUntilStart, timeUntilEnd, currentNYTime
 */
export function checkTimeRestriction(timeRestriction) {
  // IMPORTANT: Strategies without time restrictions are ALWAYS active
  if (!timeRestriction) {
    return {
      isActive: true,
      hasRestriction: false,
      timeUntilStart: null,
      timeUntilEnd: null,
      currentNYTime: null,
      restrictionWindow: null,
    };
  }

  try {
    const {
      hour_start,
      hour_end,
      minute_start,
      minute_end,
      timezone = 'America/New_York'
    } = timeRestriction;

    // Validate inputs
    if (
      typeof hour_start !== 'number' || 
      typeof hour_end !== 'number' ||
      typeof minute_start !== 'number' || 
      typeof minute_end !== 'number'
    ) {
      console.error('Invalid time restriction format:', timeRestriction);
      return {
        isActive: false,
        hasRestriction: true,
        error: 'Invalid time format',
        currentNYTime: null,
      };
    }

    // Get current time in strategy's timezone (handles DST automatically)
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const currentHour = parseInt(parts.find(p => p.type === 'hour').value);
    const currentMinute = parseInt(parts.find(p => p.type === 'minute').value);
    const currentSecond = parseInt(parts.find(p => p.type === 'second').value);

    // Get formatted current time for display
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    const currentNYTime = timeFormatter.format(now);

    // Convert times to minutes since midnight for easier comparison
    const currentMinutesSinceMidnight = currentHour * 60 + currentMinute;
    const startMinutes = hour_start * 60 + minute_start;
    const endMinutes = hour_end * 60 + minute_end;

    // Check if current time is within the restriction window
    let isActive = false;
    
    if (endMinutes >= startMinutes) {
      // Normal case: start and end on same day (e.g., 9:30 AM - 4:00 PM)
      isActive = currentMinutesSinceMidnight >= startMinutes && 
                 currentMinutesSinceMidnight <= endMinutes;
    } else {
      // Edge case: window crosses midnight (e.g., 11:00 PM - 1:00 AM)
      isActive = currentMinutesSinceMidnight >= startMinutes || 
                 currentMinutesSinceMidnight <= endMinutes;
    }

    // Calculate time until start/end
    let minutesUntilStart = 0;
    let minutesUntilEnd = 0;

    if (!isActive) {
      if (currentMinutesSinceMidnight < startMinutes) {
        // Before start time today
        minutesUntilStart = startMinutes - currentMinutesSinceMidnight;
      } else {
        // After end time, calculate until start tomorrow
        minutesUntilStart = (24 * 60) - currentMinutesSinceMidnight + startMinutes;
      }
    } else {
      // Currently active, calculate time until end
      if (endMinutes >= startMinutes) {
        minutesUntilEnd = endMinutes - currentMinutesSinceMidnight;
      } else {
        // Window crosses midnight
        if (currentMinutesSinceMidnight >= startMinutes) {
          minutesUntilEnd = (24 * 60) - currentMinutesSinceMidnight + endMinutes;
        } else {
          minutesUntilEnd = endMinutes - currentMinutesSinceMidnight;
        }
      }
    }

    // Format restriction window for display
    const formatTime = (hour, minute) => {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
    };

    const restrictionWindow = `${formatTime(hour_start, minute_start)} - ${formatTime(hour_end, minute_end)} EST`;

    return {
      isActive,
      hasRestriction: true,
      timeUntilStart: isActive ? null : minutesUntilStart,
      timeUntilEnd: isActive ? minutesUntilEnd : null,
      currentNYTime,
      restrictionWindow,
      currentHour,
      currentMinute,
      currentSecond,
      timezone,
    };
  } catch (error) {
    console.error('Error checking time restriction:', error);
    return {
      isActive: false,
      hasRestriction: true,
      error: error.message,
      currentNYTime: null,
    };
  }
}

/**
 * Format minutes into human-readable duration
 * @param {number} minutes - Number of minutes
 * @returns {string} Formatted string (e.g., "2h 15m", "45m", "5m")
 */
export function formatDuration(minutes) {
  if (!minutes || minutes < 0) return 'N/A';
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
}

/**
 * Parse time restriction from strategy config
 * @param {Object} strategy - Strategy object
 * @returns {Object|null} Time restriction object or null
 */
export function parseTimeRestriction(strategy) {
  try {
    // Check if strategy has strategy_config (might be JSON string)
    if (strategy.strategy_config) {
      const config = typeof strategy.strategy_config === 'string' 
        ? JSON.parse(strategy.strategy_config) 
        : strategy.strategy_config;
      
      return config.time_restriction || null;
    }
    
    // Check if time_restriction is directly on strategy object
    if (strategy.time_restriction) {
      return strategy.time_restriction;
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing time restriction:', error, strategy);
    return null;
  }
}

/**
 * Check if strategy should be visible based on time restriction.
 * CRITICAL: Strategies WITHOUT time_restriction are ALWAYS shown.
 * CRITICAL: Strategies with an OPEN or PENDING trade are ALWAYS shown —
 *   the position must remain visible until the trade is fully closed,
 *   even if the time restriction window has ended.
 * @param {Object} strategy - Strategy object
 * @returns {boolean} True if strategy should be shown
 */
export function isStrategyActiveNow(strategy) {
  // Always keep open/pending strategies on screen regardless of time window
  const liveStatus = strategy?.current_state?.status;
  if (liveStatus === 'open' || liveStatus === 'pending') {
    return true;
  }

  const timeRestriction = parseTimeRestriction(strategy);

  // ALWAYS show strategies without time restrictions
  if (!timeRestriction) {
    return true;
  }

  const status = checkTimeRestriction(timeRestriction);

  // If there's an error parsing, hide the strategy for safety
  if (status.error) {
    return false;
  }

  // Show if currently within the time restriction window
  return status.isActive;
}
